import { useState, useEffect } from "react";
import { MapPin, Calendar, Check, X, RefreshCw, Plus, Sparkles, Plane } from "lucide-react";
import axios from "axios";
import { API } from "../utils/constants";
import FormShell from "../components/FormShell";

// Airport → city mapping for return-leg detection (must match backend AIRPORT_TO_CITY)
const AIRPORT_TO_CITY = {
  NRT: "Tokyo", HND: "Tokyo",
  KIX: "Osaka", ITM: "Osaka",
  NGO: "Nagoya", CTS: "Sapporo",
  FUK: "Fukuoka", OKA: "Naha",
};
// Inter-city transit hours (must match backend TRANSIT_TIMES)
const INTERCITY_HOURS = {
  "Tokyo|Kyoto": 4.0, "Kyoto|Tokyo": 4.0,
  "Tokyo|Osaka": 4.5, "Osaka|Tokyo": 4.5,
  "Kyoto|Osaka": 1.5, "Osaka|Kyoto": 1.5,
};

// Airport → its city, door to door, in hours (must match backend AIRPORT_TRANSIT)
const AIRPORT_TRANSIT_HOURS = { NRT: 1.5, HND: 0.75, KIX: 1.25, ITM: 0.5, NGO: 1.0, CTS: 1.25, FUK: 0.5, OKA: 0.5 };
const PRE_FLIGHT_HOURS = 3;       // at the airport this long before the flight (backend AIRPORT_PRE_FLIGHT_BUFFER)
const FIRST_TRAIN_HOUR = 6;       // the first long-distance trains leave around 06:00
const CALM_MORNING_HOUR = 9;      // leaving the hotel from this hour on is an unhurried morning

const clock = (hours) => {
  const h = ((hours % 24) + 24) % 24;
  const whole = Math.floor(h);
  const minutes = Math.round((h - whole) * 60 / 5) * 5;
  const hh = minutes === 60 ? whole + 1 : whole;
  return `${String(hh % 24).padStart(2, "0")}:${String(minutes === 60 ? 0 : minutes).padStart(2, "0")}`;
};

// The last night before the flight home, when the trip ends in a city that is not the
// airport's: where to sleep is the traveller's choice, made knowing what each answer costs.
// From the hour of the flight we work out when they would have to leave each city, and
// recommend: the airport's city when the same-morning journey is impossible or very early,
// the trip's last city when there is time.
const lastNightAdvice = (dates, selectedCities) => {
  if (selectedCities.length < 1) return null;
  const lastCity = selectedCities[selectedCities.length - 1];
  if (!lastCity || (lastCity.days || 0) < 2) return null;
  const depAirport = dates.differentDepartureAirport ? (dates.departureAirport || dates.airport || "NRT") : (dates.airport || "NRT");
  const airportCity = AIRPORT_TO_CITY[depAirport];
  if (!airportCity || airportCity === lastCity.name) return null;
  const transitHours = INTERCITY_HOURS[`${lastCity.name}|${airportCity}`];
  if (!transitHours || transitHours < 1.5) return null;
  const [h, m] = String(dates.departureTime || "18:00").split(":").map(Number);
  const flight = (h || 0) + (m || 0) / 60;
  const atAirport = flight - PRE_FLIGHT_HOURS;
  const leaveAirportCity = atAirport - (AIRPORT_TRANSIT_HOURS[depAirport] || 1);
  const leaveLastCity = leaveAirportCity - transitHours;
  const sameMorning = leaveLastCity < FIRST_TRAIN_HOUR ? "impossible" : leaveLastCity < CALM_MORNING_HOUR ? "early" : "comfortable";
  return {
    lastCity, depAirport, airportCity, transitHours, flight, atAirport, leaveAirportCity, leaveLastCity, sameMorning,
    doorToDoor: transitHours + (AIRPORT_TRANSIT_HOURS[depAirport] || 1),
    recommended: sameMorning === "comfortable" ? "stay" : "return",
  };
};

const CitiesPage = ({ onNext, onBack, dates, setDates, selectedCities, setSelectedCities, startOver, selectedVibes }) => {
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [citiesError, setCitiesError] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  // "Help me decide" is one proposal, then they adjust (owner, 2026-10-27): the one they got stays with them
  const [aiSuggestion, setAiSuggestion] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("ju_help_suggestion") || "null"); } catch { return null; }
  });
  const [isLoadingHelp, setIsLoadingHelp] = useState(false);
  const [suggestError, setSuggestError] = useState("");

  useEffect(() => { fetchCities(); }, []);

  const fetchCities = async () => {
    setLoadingCities(true);
    setCitiesError(false);
    try {
      const res = await axios.get(`${API}/catalog/cities`);
      setCities(res.data.cities || []);
    } catch (e) {
      console.error("Failed to fetch cities", e);
      setCitiesError(true);
    } finally {
      setLoadingCities(false);
    }
  };

  const tripDays = (() => {
    if (!dates.startDate || !dates.endDate) return 0;
    const diff = Math.ceil((new Date(dates.endDate) - new Date(dates.startDate)) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  })();

  // The last night: recommended from the flight's hour until the traveller chooses for themselves.
  const lastNight = lastNightAdvice(dates, selectedCities);
  const lastNightRecommended = lastNight ? lastNight.recommended : null;
  const lastNightForced = lastNight ? lastNight.sameMorning === "impossible" : false;
  useEffect(() => {
    if (!lastNightRecommended) return;
    setDates(d => {
      const wanted = lastNightForced ? true : (d.lastNightChosen ? d.returnToAirportCity !== false : lastNightRecommended === "return");
      return d.returnToAirportCity === wanted ? d : { ...d, returnToAirportCity: wanted };
    });
  }, [lastNightRecommended, lastNightForced, setDates]);
  const chooseLastNight = (returnToAirportCity) => setDates(d => ({ ...d, returnToAirportCity, lastNightChosen: true }));

  const totalCityDays = selectedCities.reduce((sum, c) => sum + (parseInt(c.days) || 0), 0);
  const daysMatch = totalCityDays === tripDays;
  const canContinue = selectedCities.length > 0 && daysMatch;

  const addCity = (cityName) => {
    if (!selectedCities.find(c => c.name === cityName)) {
      setSelectedCities([...selectedCities, { name: cityName, days: 2 }]);
    }
  };

  const removeCity = (cityName) => setSelectedCities(selectedCities.filter(c => c.name !== cityName));

  const updateCityDays = (cityName, days) => {
    const parsed = days === '' ? '' : Math.max(1, parseInt(days) || 1);
    setSelectedCities(selectedCities.map(c => c.name === cityName ? { ...c, days: parsed } : c));
  };

  return (
    <FormShell
      step={2}
      photo="/photos/onboard-cities"
      photoPos="49% 50%"
      eyebrow="WHERE TO?"
      bigWord="WHERE?"
      title="Where to?"
      subtitle="Pick your cities and how many days each"
      onBack={onBack}
      onStartOver={startOver}
      onContinue={() => onNext()}
      continueDisabled={!canContinue}
    >
      {/* Trip days badge */}
      <div className="inline-flex items-center gap-2 self-start px-3.5 py-1.5" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 2 }} data-testid="trip-days-badge">
        <Calendar className="w-3.5 h-3.5" style={{ color: "var(--zen-accent)" }} />
        <span className="text-[13px] font-medium" style={{ color: "var(--zen-text)" }}>Your trip: {tripDays} days</span>
        {selectedCities.length > 0 && !daysMatch && (
          <span className="text-xs font-bold" style={{ color: "var(--zen-accent)", letterSpacing: "0.04em" }}>({totalCityDays}/{tripDays} allocated)</span>
        )}
      </div>

      {/* Help me decide — opens dialogue */}
      {!helpDialogOpen && (
        <button
          onClick={() => { setHelpDialogOpen(true); }}
          className="w-full cursor-pointer transition-all flex items-center gap-3 text-left hover:shadow-md"
          style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4, padding: "14px 16px" }}
          data-testid="help-me-cities-toggle">
          <div className="flex items-center justify-center shrink-0" style={{ width: 34, height: 34, borderRadius: 2, background: "var(--zen-accent)" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
          </div>
          <div>
            <p className="text-sm" style={{ color: "var(--zen-text)", fontWeight: 600 }}>Not sure? Let us help you decide</p>
            <p className="text-xs" style={{ color: "var(--zen-sub)", marginTop: 2 }}>Tell us your vibe, we'll suggest the perfect city split</p>
          </div>
          <span className="ml-auto shrink-0" style={{ color: "var(--zen-dim)", fontSize: 18 }}>›</span>
        </button>
      )}

      {/* Help me dialogue */}
      {helpDialogOpen && (
        <div className="p-5 space-y-4" style={{ background: "var(--zen-s1)", border: "2px solid var(--zen-accent)", borderRadius: 4 }} data-testid="help-dialogue">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold" style={{ color: "var(--zen-text)" }}>Help me decide</h3>
            <button onClick={() => setHelpDialogOpen(false)} className="p-1 hover:opacity-70" style={{ color: "var(--zen-dim)" }}><X className="w-4 h-4" /></button>
          </div>
          <p className="text-xs" style={{ color: "var(--zen-sub)" }}>We'll suggest the right cities and days for your {tripDays}-day trip.</p>

          {!aiSuggestion && (
            <button onClick={async () => {
              setIsLoadingHelp(true);
              setSuggestError("");
              try {
                const res = await axios.post(`${API}/suggest/city-allocation`, { trip_days: tripDays, vibes: selectedVibes });
                setAiSuggestion(res.data);
                try { sessionStorage.setItem("ju_help_suggestion", JSON.stringify(res.data)); } catch {}
              } catch (e) {
                // said as it is, never a made-up split dressed as advice (2026-10-07)
                setSuggestError(e.response?.data?.detail || "We couldn't suggest a split just now — choose your cities and days below.");
              }
              finally { setIsLoadingHelp(false); }
            }} disabled={isLoadingHelp}
              className="zen-btn zen-btn-primary w-full py-3 text-sm"
              data-testid="get-suggestion-btn">
              {isLoadingHelp ? <><RefreshCw className="w-4 h-4 animate-spin" /> Thinking...</> : <><Sparkles className="w-4 h-4" /> Suggest my trip</>}
            </button>
          )}

          {suggestError && !aiSuggestion && (
            <p role="alert" className="text-xs mt-2" style={{ color: "var(--zen-accent)" }}>{suggestError}</p>
          )}

          {aiSuggestion && (
            <div className="space-y-3">
              <p className="text-sm" style={{ color: "var(--zen-sub)" }}>{aiSuggestion.suggestion}</p>
              {aiSuggestion.travel_days_note && <p className="text-xs italic" style={{ color: "var(--zen-dim)" }}>{aiSuggestion.travel_days_note}</p>}

              <div className="space-y-2">
                {aiSuggestion.cities?.map((city, i) => (
                  <div key={i} className="flex items-center gap-3 rounded p-3" style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)" }}>
                    <MapPin className="w-4 h-4 shrink-0" style={{ color: "var(--zen-accent)" }} />
                    <div className="flex-1">
                      <span className="font-medium text-sm" style={{ color: "var(--zen-text)" }}>{city.name}</span>
                      <span className="text-xs ml-2" style={{ color: "var(--zen-dim)" }}>{city.reason}</span>
                    </div>
                    <span className="text-sm font-medium" style={{ color: "var(--zen-accent)" }}>{city.days} days</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <button onClick={() => {
                  setSelectedCities(aiSuggestion.cities.map(c => ({ name: c.name, days: c.days })));
                  setHelpDialogOpen(false);
                }}
                  className="zen-btn zen-btn-primary flex-1 py-3 text-sm"
                  data-testid="accept-suggestion">
                  <Check className="w-4 h-4" /> Use this plan
                </button>
                <button onClick={() => setHelpDialogOpen(false)}
                  className="zen-btn zen-btn-outline flex-1 py-3 text-sm"
                  data-testid="adjust-myself">
                  I'll adjust it myself
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Selected cities */}
      {selectedCities.length > 0 && (
        <div className="space-y-3">
          {selectedCities.map((city) => (
            <div key={city.name} className="p-4" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4 }}>
              <div className="flex items-center gap-4">
                <span className="font-medium flex-1" style={{ color: "var(--zen-text)" }}>{city.name}</span>
                <div className="flex items-center gap-2">
                  <input type="number" min="1" value={city.days}
                    onChange={(e) => updateCityDays(city.name, e.target.value)}
                    onBlur={(e) => { if (e.target.value === '') updateCityDays(city.name, '1'); }}
                    className="w-14 rounded px-2 py-1 text-center text-sm" style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)", color: "var(--zen-text)" }} data-testid={`city-days-${city.name}`} />
                  <span className="text-sm" style={{ color: "var(--zen-sub)" }}>days</span>
                </div>
                <button onClick={() => removeCity(city.name)} className="p-1 hover:opacity-70 transition-opacity" style={{ color: "var(--zen-dim)" }}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              {/* Where they sleep — optional. Drives "back to the hotel" minutes before a big dinner. */}
              <input type="text" value={city.base || ""} placeholder={`Where are you staying in ${city.name}? (hotel or area — optional)`}
                onChange={(e) => setSelectedCities(selectedCities.map(c => c.name === city.name ? { ...c, base: e.target.value } : c))}
                className="w-full rounded px-3 py-2 text-sm mt-3" style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)", color: "var(--zen-text)" }} data-testid={`city-base-${city.name}`} />
            </div>
          ))}

          {tripDays > 0 && (
            <div className="p-3 rounded text-sm font-medium"
              style={{
                background: daysMatch ? "var(--zen-accent)" : totalCityDays > tripDays ? "var(--cat-nightlife)" : "var(--zen-s2)",
                color: daysMatch || totalCityDays > tripDays ? "#fff" : "var(--zen-sub)",
                border: `1px solid ${daysMatch ? "var(--zen-accent)" : totalCityDays > tripDays ? "var(--cat-nightlife-solid)" : "var(--zen-border)"}`
              }} data-testid="days-validation">
              {daysMatch ? `All ${tripDays} days allocated` : totalCityDays > tripDays
                ? `${totalCityDays - tripDays} days over your ${tripDays}-day trip` : `${totalCityDays} of ${tripDays} days — ${tripDays - totalCityDays} remaining`}
            </div>
          )}
        </div>
      )}

      {/* Available cities */}
      {loadingCities && (
        <div className="flex items-center justify-center gap-2 py-6" data-testid="cities-loading">
          <RefreshCw className="w-4 h-4 animate-spin" style={{ color: "var(--zen-accent)" }} />
          <span className="text-sm" style={{ color: "var(--zen-sub)" }}>Loading cities...</span>
        </div>
      )}
      {citiesError && !loadingCities && (
        <div className="text-center py-6 space-y-3" data-testid="cities-error">
          <p className="text-sm" style={{ color: "var(--cat-foodie-solid)" }}>Could not load cities</p>
          <button onClick={fetchCities} className="zen-btn zen-btn-outline text-sm px-4 py-2" data-testid="cities-retry-btn">
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      )}
      {!loadingCities && !citiesError && (
        <div className="flex flex-wrap gap-2" data-testid="available-cities">
          {cities.filter(c => !selectedCities.find(sc => sc.name === c.name)).map((city) => (
            <button key={city.name} onClick={() => addCity(city.name)}
              className="flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-all hover:shadow-sm"
              style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", color: "var(--zen-text)" }}
              data-testid={`add-city-${city.name}`}>
              <Plus className="w-3 h-3" /> {city.name}
            </button>
          ))}
        </div>
      )}

      {/* The last night before the flight home. Shown when the trip ends in a city that is not
          the airport's and the journey between them is a real one (>= 1.5h). The answer is the
          traveller's: each option says what it costs, worked out from the hour of their flight
          (lastNightAdvice above). One is recommended, with its reason; when the same-morning
          journey cannot be made by train, that option is shown and closed. */}
      {lastNight && (() => {
        const { lastCity, depAirport, airportCity, sameMorning, recommended } = lastNight;
        const returning = dates.returnToAirportCity !== false;
        const daysLeft = lastCity.days - 1;
        const veryEarlyAnyway = lastNight.leaveAirportCity < FIRST_TRAIN_HOUR;
        const option = (selected, disabled) => ({
          display: "block", width: "100%", textAlign: "left", padding: "14px 16px", borderRadius: 4,
          background: selected ? "var(--zen-bg, transparent)" : "transparent",
          border: `1px solid ${selected ? "var(--zen-accent)" : "var(--zen-border)"}`,
          opacity: disabled ? 0.55 : 1, cursor: disabled ? "not-allowed" : "pointer",
        });
        const badge = (
          <span className="ml-2 text-xs" style={{ color: "var(--zen-accent)", letterSpacing: "0.04em" }}>RECOMMENDED</span>
        );
        const mark = (selected) => (
          <span aria-hidden="true" style={{
            display: "inline-block", width: 14, height: 14, borderRadius: "50%", marginRight: 10, verticalAlign: "-2px",
            border: `1px solid ${selected ? "var(--zen-accent)" : "var(--zen-dim)"}`,
            background: selected ? "var(--zen-accent)" : "transparent", boxShadow: selected ? "inset 0 0 0 3px var(--zen-s1)" : "none",
          }} />
        );
        return (
          <div className="p-5" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4 }} data-testid="last-night-choice">
            <div className="flex items-start gap-3 mb-4">
              <Plane className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--zen-accent)" }} />
              <div className="flex-1 text-sm" style={{ color: "var(--zen-text)" }}>
                <strong>Where do you sleep the last night?</strong>
                <span className="block mt-1" style={{ color: "var(--zen-sub)" }}>
                  Your flight leaves <strong style={{ color: "var(--zen-text)" }}>{depAirport}</strong> ({airportCity}) at {clock(lastNight.flight)},
                  so you need to be at the airport around {clock(lastNight.atAirport)}. Your trip ends in <strong style={{ color: "var(--zen-text)" }}>{lastCity.name}</strong>,
                  about {lastNight.doorToDoor}h from the airport door to door.
                </span>
              </div>
            </div>
            <div className="space-y-2" role="radiogroup" aria-label="Where to sleep the last night">
              <button type="button" role="radio" aria-checked={returning} onClick={() => chooseLastNight(true)}
                style={option(returning, false)} data-testid="last-night-return">
                <span className="text-sm" style={{ color: "var(--zen-text)" }}>
                  {mark(returning)}<strong>In {airportCity}, close to the airport</strong>{recommended === "return" && badge}
                </span>
                <span className="block mt-1 text-xs" style={{ color: "var(--zen-dim)", paddingLeft: 24 }}>
                  You travel from {lastCity.name} the afternoon before, so {lastCity.name} keeps {daysLeft} full day{daysLeft > 1 ? "s" : ""}.
                  {" "}On the day of the flight you leave your hotel around {clock(lastNight.leaveAirportCity)}.
                  {veryEarlyAnyway && ` That is before the first trains: book the last night at a hotel next to ${depAirport}.`}
                </span>
              </button>
              <button type="button" role="radio" aria-checked={!returning} aria-disabled={sameMorning === "impossible"}
                onClick={() => { if (sameMorning !== "impossible") chooseLastNight(false); }}
                style={option(!returning, sameMorning === "impossible")} data-testid="last-night-stay">
                <span className="text-sm" style={{ color: "var(--zen-text)" }}>
                  {mark(!returning)}<strong>In {lastCity.name}, to the very end</strong>{recommended === "stay" && badge}
                </span>
                <span className="block mt-1 text-xs" style={{ color: "var(--zen-dim)", paddingLeft: 24 }}>
                  {sameMorning === "comfortable" && `You have time: every day of ${lastCity.name} stays whole, and on the day of the flight you leave around ${clock(lastNight.leaveLastCity)} and go straight to the airport.`}
                  {sameMorning === "early" && `Possible, but an early start: you leave ${lastCity.name} around ${clock(lastNight.leaveLastCity)} with your bags, and a delayed train can cost you the flight.`}
                  {sameMorning === "impossible" && `Not possible by train: you would have to leave ${lastCity.name} around ${clock(lastNight.leaveLastCity)}${lastNight.leaveLastCity < 0 ? " the night before" : ""}, before the first trains run (about 06:00).`}
                </span>
              </button>
            </div>
          </div>
        );
      })()}

      {/* Landing-city detector (arrival twin of the block above). Fires when
          the landing airport's city is not the first city of the trip
          (land NRT, trip starts in Kyoto). Default: go straight there —
          the brain builds Day 1 around the transfer. Option: first night
          in the landing city (adds it as a 1-day city at the start). */}
      {(() => {
        if (selectedCities.length < 1) return null;
        const firstCity = selectedCities[0];
        const landingCity = AIRPORT_TO_CITY[dates.airport || "NRT"];
        if (!landingCity || landingCity === firstCity.name) return null;
        const hours = INTERCITY_HOURS[`${landingCity}|${firstCity.name}`];
        if (!hours || hours < 1.5) return null;
        const addFirstNight = () => {
          const rest = selectedCities.map((c, i) => (i === 0 && c.days > 1 ? { ...c, days: c.days - 1 } : c));
          setSelectedCities([{ name: landingCity, days: 1 }, ...rest]);
        };
        return (
          <div className="p-5" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4 }}>
            <div className="flex items-start gap-3 mb-3">
              <Plane className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--zen-accent)" }} />
              <div className="flex-1 text-sm" style={{ color: "var(--zen-text)" }}>
                You land at <strong>{dates.airport || "NRT"}</strong> ({landingCity}) but your trip starts in <strong>{firstCity.name}</strong>.
                The transfer takes ~{hours}h, so we'll build your first day around it: land, train, settle in, one gentle evening.
              </div>
            </div>
            <button type="button" onClick={addFirstNight}
              className="text-sm font-medium hover:opacity-80 transition-opacity"
              style={{ color: "var(--zen-accent)", background: "none", border: "1px solid var(--zen-accent)", borderRadius: 4, padding: "8px 12px", cursor: "pointer" }}
              data-testid="first-night-landing-city">
              Prefer a first night in {landingCity}? Add it as a 1-day stop →
            </button>
          </div>
        );
      })()}

      {/* Allocation hints (shown above the Continue button) */}
      {!canContinue && selectedCities.length > 0 && (
        <p className="text-sm text-center font-medium" style={{ color: "var(--cat-foodie-solid)" }} data-testid="allocation-hint">
          {totalCityDays > tripDays
            ? `Remove ${totalCityDays - tripDays} day${totalCityDays - tripDays > 1 ? 's' : ''} to match your ${tripDays}-day trip`
            : `Allocate ${tripDays - totalCityDays} more day${tripDays - totalCityDays > 1 ? 's' : ''} — add a city or increase days above`}
        </p>
      )}
      {!canContinue && selectedCities.length === 0 && (
        <p className="text-sm text-center font-medium" style={{ color: "var(--cat-foodie-solid)" }}>
          Pick at least one city, or let us help you decide
        </p>
      )}
    </FormShell>
  );
};

export default CitiesPage;
